import { Logger, serializeError } from 'logging';

import {
    AudioDetailsResponse,
    AudioSearchResponse,
    DetailsApi,
    GetSimilarAudioItems200Response,
    GraphicSearchResponse,
    SearchSimilarApi,
    SearchSimilarApiGetSimilarAudioItemsRequest,
    TypeaheadApi,
    TypeaheadResponse,
    VideoSearchResponse,
    WebSearchApi,
    WebSearchApiSearchAudioRequest,
    WebSearchApiSearchGraphicRequest,
    WebSearchApiSearchVideoRequest,
} from '@videoblocks/search-gateway-client';

import { defaultAlgorithm, getStringifiedAlgorithm } from './algorithms';
import { paginationRule } from './rules';
import { BaseSearchRequest, ISearchGatewayClient, Rule } from './types';

export default abstract class BaseSearchGatewayClient implements ISearchGatewayClient {
    public abstract searchAudio(props: WebSearchApiSearchAudioRequest): Promise<AudioSearchResponse | undefined>;
    public abstract searchVideo(props: WebSearchApiSearchVideoRequest): Promise<VideoSearchResponse | undefined>;
    public abstract searchImages(props: WebSearchApiSearchGraphicRequest): Promise<GraphicSearchResponse | undefined>;
    public abstract getSearchAudioRequest(props: WebSearchApiSearchAudioRequest): WebSearchApiSearchAudioRequest;
    public abstract getTypeAheadSuggestion(
        contentClass: 'video' | 'audio' | 'graphic',
        keywords?: string,
    ): Promise<TypeaheadResponse | undefined>;
    public abstract searchSimilarAudio(
        params: SearchSimilarApiGetSimilarAudioItemsRequest,
    ): Promise<GetSimilarAudioItems200Response | undefined>;
    public abstract getAudioDetailsByUrlId(urlId: string): Promise<AudioDetailsResponse>;

    constructor(
        readonly logger: Logger,
        readonly api: WebSearchApi,
        readonly typeaheadApi: TypeaheadApi,
        readonly searchSimilarApi: SearchSimilarApi,
        readonly detailsApi: DetailsApi,
    ) {}

    buildRequest<Req extends BaseSearchRequest>(
        { searchTerm, numResults = 20, ...otherParams }: Req,
        rules: Record<string, Rule> = { pageNumber: paginationRule },
    ): Req {
        const base: BaseSearchRequest = {
            algorithm: getStringifiedAlgorithm(defaultAlgorithm),
            searchTerm,
            numResults,
        };
        const req = base as Partial<Req>;

        for (const targetKey of Object.keys(rules)) {
            const { searchKey, transform } = rules[targetKey];
            const raw = (otherParams as any)?.[searchKey];

            if (raw !== undefined) {
                (req as any)[targetKey] = transform ? transform(raw) : raw;
            }
        }

        return req as Req;
    }

    logError(error: any, props: unknown = {}): void {
        // todo: better logging for Axios errors
        const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
        this.logger.warn({ error: serializeError(error), props }, message ?? 'Error searching for audio');
    }
}
